import {
  ONBOARDING_EXCLUDED_QUERY_PARAMS,
  ONBOARDING_EXCLUDED_ROUTES,
} from "../constants";

/**
 * Whether the landing URL is one the flow keeps out of, captured on the first
 * call and frozen for the life of the tab.
 *
 * `undefined` until something asks, which on the client is the first render of
 * anything reading `onboardingV2OpenAtom` — i.e. the root layout, at landing.
 */
let excluded: boolean | undefined;

/**
 * Whether the URL this browser session landed on is one the flow keeps out of:
 * a venue deep link, a password reset, an email verification, or campaign
 * traffic carrying UTM parameters.
 *
 * Read from `window.location` rather than the router, and answered
 * **synchronously** rather than from an effect, because two things depend on
 * it on the very first render: whether the onboarding modal opens at all, and
 * whether `useGeoLocation` may raise the browser's location prompt. An effect
 * would lose that race — React runs child effects before their parents, so the
 * home page's `useGeoLocation` fires before anything mounted at the root could
 * have decided.
 *
 * Frozen after the first answer on purpose: navigating from an excluded page to
 * the home page should not spring a four-step introduction on someone who came
 * for a venue. That is the rule the carousel this replaces followed too — its
 * `initialRouteRef` was likewise set once.
 *
 * `true` on the server, where there is no window to read. Refusing to open is
 * the right default for a decision that cannot be made yet, and the answer is
 * discarded before the client's first render ever asks.
 */
export const isLandingRouteExcluded = (): boolean => {
  if (typeof window === "undefined") return true;
  if (excluded !== undefined) return excluded;

  const url = new URL(window.location.href);

  const routeExcluded = ONBOARDING_EXCLUDED_ROUTES.some((route) =>
    url.pathname.includes(route),
  );
  const queryExcluded = ONBOARDING_EXCLUDED_QUERY_PARAMS.some((param) =>
    url.searchParams.has(param),
  );

  excluded = routeExcluded || queryExcluded;
  return excluded;
};
